import Route from '@ember/routing/route'
import type Store from '@ember-data/store'
import { service } from '@ember/service'
import { task } from 'ember-concurrency'
import type BookLibraryShare from 'teacher-ui-client/models/book-library/share'
import { ShareStatus } from 'teacher-ui-client/models/book-library/share'
import type Teacher from 'teacher-ui-client/models/teacher'

interface Params extends Record<string, unknown> {
  student_id: string
}

export default class BookLibrarySharesByStudentShowRoute extends Route {
  @service store!: Store

  /**
   * Request all Active and Pending shares for a single student.
   */
  fetchSharesForStudent = task({ restartable: true }, async ({ studentId }: { studentId: string }) => {
    const shares = (
      await this.store.query<BookLibraryShare>('book-library/share', {
        filter: { student_ids: [studentId], status: [ShareStatus.Pending, ShareStatus.Active].join(',') },
        // @ts-expect-error - include types not correct
        include: 'collection,collection.books,collection.books.book-metadata',
      })
    ).slice()
    // Each share has a "shared by" teacher - need their names for the table.
    const uniqueTeacherIds = shares.reduce<string[]>((teacherIds, share) => {
      if (!share.sharedByUserId) return teacherIds
      return teacherIds.includes(share.sharedByUserId) ? teacherIds : [...teacherIds, share.sharedByUserId]
    }, [])
    // The shares are for one student, so shouldn't be too many teachers requested here.
    if (!uniqueTeacherIds) return { shares, teachers: [] }
    const teachers = (await this.store.query<Teacher>('teacher', { filter: { ids: uniqueTeacherIds } })).slice()

    return { shares, teachers }
  })

  model(params: Params) {
    const { subscriptionType } = this.modelFor('subscription-type') as ModelForSubscriptionTypeRoute
    void this.fetchSharesForStudent.perform({ studentId: params.student_id })

    return {
      studentId: params.student_id,
      subscriptionType,
      fetchSharesForStudent: this.fetchSharesForStudent,
    }
  }
}
